// src/components/SPanel/Home/homeWeather.js
//
// Vremea la lecția următoare: prognoza pe ore (Open-Meteo, coduri WMO) pentru
// sectorul lecției, rezumată pe orele lecției, cu sfaturi pentru volan.

/** Chișinău, centru: când sectorul lipsește sau nu e cunoscut. */
export const CITY_COORDS = [47.0105, 28.8638];

const SECTOR_COORDS = {
   botanica: [46.986, 28.857],
   ciocana: [47.041, 28.894],
   buiucani: [47.033, 28.796],
   centru: [47.024, 28.833],
   rascani: [47.053, 28.861], // Râșcani
   riscani: [47.053, 28.861],
};

function plain(text) {
   return String(text || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "");
}

export function coordsForSector(sector) {
   const name = plain(sector);
   const key = Object.keys(SECTOR_COORDS).find((k) => name.includes(k));
   return key ? SECTOR_COORDS[key] : CITY_COORDS;
}

/** Felul vremii după codul WMO (câteva categorii, pentru iconiță și sfaturi). */
export function weatherKind(code) {
   const c = Number(code);
   if (c === 0) return "clear";
   if (c === 1 || c === 2) return "partly";
   if (c === 3) return "cloudy";
   if (c === 45 || c === 48) return "fog";
   if (c === 56 || c === 57 || c === 66 || c === 67) return "freezing";
   if (c >= 51 && c <= 55) return "drizzle";
   if ((c >= 61 && c <= 65) || (c >= 80 && c <= 82)) return "rain";
   if ((c >= 71 && c <= 77) || c === 85 || c === 86) return "snow";
   if (c >= 95) return "storm";
   return "cloudy";
}

const LABELS = {
   0: "Senin",
   1: "Mai mult senin",
   2: "Parțial noros",
   3: "Înnorat",
   45: "Ceață",
   48: "Ceață cu chiciură",
   51: "Burniță slabă",
   53: "Burniță",
   55: "Burniță deasă",
   56: "Burniță înghețată",
   57: "Burniță înghețată",
   61: "Ploaie slabă",
   63: "Ploaie",
   65: "Ploaie puternică",
   66: "Ploaie înghețată",
   67: "Ploaie înghețată",
   71: "Ninsoare slabă",
   73: "Ninsoare",
   75: "Ninsoare puternică",
   77: "Grăunțe de zăpadă",
   80: "Averse slabe",
   81: "Averse",
   82: "Averse puternice",
   85: "Averse de ninsoare",
   86: "Averse de ninsoare",
   95: "Furtună",
   96: "Furtună cu grindină",
   99: "Furtună cu grindină",
};

export function weatherLabel(code) {
   return LABELS[Number(code)] || "Înnorat";
}

// cât de „rea” e vremea pentru condus: pe lecție contează cea mai rea oră
const SEVERITY = {
   clear: 0,
   partly: 1,
   cloudy: 2,
   drizzle: 3,
   fog: 4,
   rain: 5,
   snow: 6,
   freezing: 7,
   storm: 8,
};

const pad = (n) => String(n).padStart(2, "0");

/** „2026-10-09T10:00”, ora locală, cum le dă Open-Meteo cu timezone. */
export function hourKey(date) {
   return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:00`;
}

/**
 * Orele lecției (de la ora începutului până înainte de sfârșit) din
 * prognoza pe ore; null dacă prognoza nu le are (lecție prea departe).
 */
export function summarizeHours(hourly, start, end) {
   const times = hourly?.time || [];
   const first = times.indexOf(hourKey(start));
   if (first < 0) return null;
   const endMs = (end || new Date(start.getTime() + 90 * 60000)).getTime();
   const indices = [];
   for (let i = first; i < times.length; i += 1) {
      if (i > first && new Date(times[i]).getTime() >= endMs) break;
      indices.push(i);
   }
   const pick = (name) => indices.map((i) => Number(hourly[name]?.[i])).filter(Number.isFinite);
   const codes = pick("weather_code");
   const worst = codes.reduce(
      (acc, code) => (SEVERITY[weatherKind(code)] > SEVERITY[weatherKind(acc)] ? code : acc),
      codes[0] ?? 3,
   );
   const temps = pick("temperature_2m");
   return {
      temp: temps[0],
      tempMin: Math.min(...temps),
      tempMax: Math.max(...temps),
      feels: pick("apparent_temperature")[0],
      code: worst,
      kind: weatherKind(worst),
      label: weatherLabel(worst),
      precipProb: Math.max(0, ...pick("precipitation_probability")),
      precip: pick("precipitation").reduce((sum, v) => sum + v, 0),
      wind: Math.max(0, ...pick("wind_speed_10m")),
      visibility: Math.min(...pick("visibility"), Infinity),
      isDay: pick("is_day")[0] !== 0,
   };
}

/** Ce merită știut la volan (cel mult două, cele mai importante întâi). */
export function drivingTips(w) {
   if (!w) return [];
   const tips = [];
   const icy = w.kind === "freezing" || (w.tempMin <= 1 && ["rain", "drizzle", "snow"].includes(w.kind));
   if (w.kind === "storm") tips.push("Furtună: vizibilitate slabă și rafale, mergi încet.");
   if (icy) tips.push("Pericol de polei: pornește și frânează foarte lin.");
   if (w.kind === "snow") tips.push("Zăpadă: viteză mică, distanță dublă față de mașina din față.");
   if (w.kind === "fog" || w.visibility < 1000) tips.push("Ceață: aprinde farurile de ceață și mergi încet.");
   if (w.kind === "rain" || w.kind === "drizzle" || w.precipProb >= 60) {
      tips.push("Drum ud: păstrează o distanță mai mare și frânează din timp.");
   }
   if (w.wind >= 40) tips.push("Vânt puternic: ține volanul ferm, mai ales pe poduri.");
   if (w.tempMax >= 28) tips.push("Cald: ia apă cu tine.");
   if (w.tempMin <= 0 && !icy) tips.push("Frig: dezaburește parbrizul înainte de plecare.");
   if ((w.kind === "clear" || w.kind === "partly") && w.isDay) {
      tips.push("Soare: ochelarii de soare te ajută la volan.");
   }
   if (!tips.length) tips.push("Vreme bună de condus.");
   return tips.slice(0, 2);
}

/** Câte zile de prognoză trebuie cerute ca să cuprindă data (Open-Meteo dă cel mult 16). */
export function forecastDaysFor(date, now = new Date()) {
   const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
   const ahead = Math.round((startOfDay(date) - startOfDay(now)) / (24 * 60 * 60 * 1000));
   if (ahead < 0) return 1;
   if (ahead > 15) return null;
   return ahead + 1;
}
